import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { CandidatePipelineStatus } from 'client/types.gen';
import {
  zCandidatePipelineStatus,
  zDeclinedStatusFilter,
  zIntroductionStatusFilter,
  zRecommendationStatusFilter,
  zSortDirection,
  zTeamCandidatesSortColumn,
} from 'client/zod.gen';
import { zBooleanFiltersArray, zFiltersArray } from 'helpers/router';

/** Default `pipeline_status` when opening the internal tab (same set as team `_internal`). */
export const USER_PIPELINE_INTERNAL_DEFAULT_PIPELINE_STATUSES = [
  CandidatePipelineStatus.SUGGESTED,
  CandidatePipelineStatus.READY_FOR_REVIEW,
  CandidatePipelineStatus.APPROVED,
  CandidatePipelineStatus.DENIED,
  CandidatePipelineStatus.PUBLISHED,
  CandidatePipelineStatus.SCHEDULED,
  CandidatePipelineStatus.SAVED,
] as const;

const internalDefaultPipelineStatus = [
  ...USER_PIPELINE_INTERNAL_DEFAULT_PIPELINE_STATUSES,
];

export const userPipelineDetailsSearchSchema = z.object({
  tab: z.enum(['external', 'internal']).optional().catch(undefined),
  // Drives the shared recommendation-detail dialogs (e.g. request intro), same
  // as the team pipeline routes.
  dialog: z.string().optional().catch(undefined),
  advisor_id: zFiltersArray().optional().catch(undefined),
  declined_status: zFiltersArray(zDeclinedStatusFilter),
  introduction_status: zFiltersArray(zIntroductionStatusFilter),
  is_accepting_intros: zBooleanFiltersArray(),
  recommendation_status: zFiltersArray(zRecommendationStatusFilter),
  underrepresented: zBooleanFiltersArray(),
  warm: zBooleanFiltersArray(),
  pipeline_status: zFiltersArray(zCandidatePipelineStatus)
    .optional()
    .catch(undefined),
  order: zSortDirection.optional().catch('desc').default('desc'),
  sort: zTeamCandidatesSortColumn.optional().catch(undefined),
});

export type UserPipelineDetailsSearch = z.infer<
  typeof userPipelineDetailsSearchSchema
>;

export const Route = createFileRoute(
  '/_private/_route/users/$userId/pipelines/$pipelineId/',
)({
  beforeLoad: ({ search, params }) => {
    if (search.tab !== 'internal') {
      return;
    }
    if (search.pipeline_status && search.pipeline_status.length > 0) {
      return;
    }
    throw redirect({
      replace: true,
      params,
      search: {
        ...search,
        tab: 'internal',
        pipeline_status: internalDefaultPipelineStatus,
        order: search.order ?? 'desc',
      },
      to: '/users/$userId/pipelines/$pipelineId',
    });
  },
  validateSearch: userPipelineDetailsSearchSchema,
});
